/**
 * MrRepo sobre Supabase. Misma lógica que DexieMrRepo (Material Requisitions).
 */

import type { Mr, EstadoMr, MrHistorialEntry } from "@app-types/entities";
import type { MrRepo } from "@core/data/repos/MrRepo";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseMrRepo extends SupabaseBaseRepo<Mr> implements MrRepo {
  constructor(tenantId: string) {
    super("mr", tenantId);
  }

  async list(filter?: { contratoMarcoId?: string; afeId?: string; estado?: EstadoMr }): Promise<Mr[]> {
    return (await this.listAll())
      .filter((m) => !filter?.contratoMarcoId || m.contratoMarcoId === filter.contratoMarcoId)
      .filter((m) => !filter?.afeId || m.afeId === filter.afeId)
      .filter((m) => !filter?.estado || m.estado === filter.estado)
      .sort((a, b) => b.fechaEmision.localeCompare(a.fechaEmision));
  }

  async get(id: string): Promise<Mr | null> {
    return this.getById(id);
  }

  async create(
    data: Omit<
      Mr,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy" | "codigo" | "historial"
    >,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const ano = now.slice(0, 4);
    const delAno = (await this.listAll()).filter((m) => m.codigo.includes(`MR-${ano}-`));
    const codigo = `MR-${ano}-${String(delAno.length + 1).padStart(3, "0")}`;
    const entry: Mr = {
      ...data,
      id,
      tenantId: this.tenantId,
      codigo,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      version: 1,
      historial: [],
    };
    await this.createRaw(entry);
    return id;
  }

  async update(id: string, data: Partial<Mr>, expectedVersion: number): Promise<void> {
    const actual = await this.get(id);
    if (!actual) throw new Error("MR no encontrada");
    if (actual.version !== expectedVersion) {
      throw new Error(
        `Conflicto de versión: el MR fue modificado por otro usuario (v${actual.version} vs v${expectedVersion}).`,
      );
    }
    const updated: Mr = {
      ...actual,
      ...data,
      id: actual.id,
      tenantId: actual.tenantId,
      codigo: actual.codigo,
      createdAt: actual.createdAt,
      updatedAt: new Date().toISOString(),
      version: actual.version + 1,
    };
    await this.putRaw(updated);
  }

  async changeState(id: string, nextState: EstadoMr, userEmail: string, observaciones?: string): Promise<void> {
    const actual = await this.get(id);
    if (!actual) throw new Error("MR no encontrada");
    const now = new Date().toISOString();
    const entry: MrHistorialEntry = {
      fecha: now,
      estadoAnterior: actual.estado,
      estadoNuevo: nextState,
      usuario: userEmail,
      ...(observaciones ? { observaciones } : {}),
    };
    const updated: Mr = {
      ...actual,
      estado: nextState,
      historial: [...(actual.historial ?? []), entry],
      ...(actual.estado === "BORRADOR" && nextState === "EMITIDA" ? { fechaEmision: now.slice(0, 10) } : {}),
      updatedAt: now,
      updatedBy: userEmail,
      version: actual.version + 1,
    };
    await this.putRaw(updated);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
